import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { clearAuthData } from '../../utils/tokenStorage';
import LoadingSpinner from './LoadingSpinner';

const classifyError = (error) => {
  if (!error) return 'unknown';

  if (error.status) {
    if (error.status >= 500) return 'server_error';
    if (error.status === 401 || error.status === 403) return 'auth_error';
    if (error.status >= 400 && error.status < 500) return 'client_error';
  }

  if (error.message) {
    const msg = error.message.toLowerCase();
    if (msg.includes('network') || msg.includes('timeout') || msg.includes('econnrefused') || msg.includes('failed to fetch')) {
      return 'network_error';
    }
  }

  return 'unknown';
};

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;

const SubscriptionGuard = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [transientError, setTransientError] = useState(null);
  const [retryDisplay, setRetryDisplay] = useState(0);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);
  const checkInProgressRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const clearTransientError = useCallback(() => {
    setTransientError(null);
    sessionStorage.removeItem('subscriptionCheckError');
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!mountedRef.current || checkInProgressRef.current) return;
    checkInProgressRef.current = true;

    if (user?.role === 'superadmin') {
      setHasActiveSubscription(true);
      setTransientError(null);
      setLoading(false);
      setLastCheck(new Date());
      checkInProgressRef.current = false;
      return;
    }

    if (!user?.companyId) {
      setHasActiveSubscription(false);
      setTransientError(null);
      setLoading(false);
      setLastCheck(new Date());
      checkInProgressRef.current = false;
      return;
    }

    try {
      const timestamp = new Date().getTime();
      const response = await subscriptionService.getMyCompanySubscription({ _t: timestamp });

      if (!mountedRef.current) {
        checkInProgressRef.current = false;
        return;
      }

      if (response.success && response.data) {
        const subscription = response.data;
        const isActive = subscription.status === 'active';
        const currentDate = new Date();
        const endDate = subscription.endDate ? new Date(subscription.endDate) : null;
        const isNotExpired = !endDate || endDate > currentDate;
        const hasActive = isActive && isNotExpired;

        setHasActiveSubscription(hasActive);
        setTransientError(null);
        setRetryDisplay(0);
        retryCountRef.current = 0;
      } else {
        setHasActiveSubscription(false);
        setTransientError(null);
      }
    } catch (error) {
      if (!mountedRef.current) {
        checkInProgressRef.current = false;
        return;
      }

      const errorType = classifyError(error);

      if (errorType === 'auth_error') {
        clearAuthData();
        setTransientError({ type: 'auth_error', message: error?.message || 'Authentication failed' });
        setLoading(false);
        checkInProgressRef.current = false;
        return;
      }

      if (errorType === 'server_error' || errorType === 'network_error' || errorType === 'unknown') {
        const errorData = {
          type: errorType,
          message: error?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        };
        setTransientError(errorData);
        sessionStorage.setItem('subscriptionCheckError', JSON.stringify(errorData));

        retryCountRef.current += 1;
        setRetryDisplay(retryCountRef.current);

        if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
          setLoading(true);
          checkInProgressRef.current = false;
          setTimeout(() => {
            if (mountedRef.current) checkSubscription();
          }, RETRY_DELAY_MS);
          return;
        }

        setHasActiveSubscription(false);
        checkInProgressRef.current = false;
        return;
      }

      setHasActiveSubscription(false);
      setTransientError(null);
      checkInProgressRef.current = false;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setLastCheck(new Date());
      }
      checkInProgressRef.current = false;
    }
  }, [user, clearTransientError]);

  useEffect(() => {
    retryCountRef.current = 0;
    setRetryDisplay(0);
    checkSubscription();
  }, [checkSubscription]);

  const refreshSubscription = useCallback(async () => {
    retryCountRef.current = 0;
    setRetryDisplay(0);
    setTransientError(null);
    sessionStorage.removeItem('subscriptionCheckError');
    setLoading(true);
    checkInProgressRef.current = false;
    await checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    const handleSubscriptionRefresh = () => {
      refreshSubscription();
    };
    window.addEventListener('subscription:refresh', handleSubscriptionRefresh);
    return () => {
      window.removeEventListener('subscription:refresh', handleSubscriptionRefresh);
    };
  }, [refreshSubscription]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && lastCheck && mountedRef.current) {
        const timeSinceLastCheck = new Date() - lastCheck;
        if (timeSinceLastCheck > 5 * 60 * 1000) {
          retryCountRef.current = 0;
          setRetryDisplay(0);
          checkInProgressRef.current = false;
          checkSubscription();
        }
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [loading, lastCheck, checkSubscription]);

  if (loading) {
    const spinnerText = retryDisplay > 0
      ? `Vérification de l'abonnement... (tentative ${retryDisplay}/${MAX_RETRY_ATTEMPTS})`
      : "Vérification de l'abonnement...";
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" color="blue" text={spinnerText} />
      </div>
    );
  }

  if (transientError?.type === 'auth_error') {
    return <Navigate to="/login" replace />;
  }

  if (transientError && (transientError.type === 'server_error' || transientError.type === 'network_error') && retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
    let redirectUrl = '/subscription-renewal';
    const params = new URLSearchParams();
    params.set('error', transientError.type);
    redirectUrl += `?${params.toString()}`;
    return <Navigate to={redirectUrl} replace />;
  }

  if (hasActiveSubscription === false && !transientError) {
    return <Navigate to="/subscription-renewal" replace />;
  }

  if (hasActiveSubscription === true) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <LoadingSpinner size="large" color="blue" text="Vérification de l'abonnement..." />
    </div>
  );
};

export default SubscriptionGuard;
